import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Fragment, useRef } from 'react';
import type { Habit } from '../../shared/api';
import { addMonths, monthGrid, sameMonth, startOfMonth, type DayKey } from '../../shared/days';
import { WEEKDAY_SHORT, dayState, firstDay, weekCount, type DayState } from '../../shared/schedule';
import { formatFullDate, formatMonth } from '../lib/format';

const STATE_LABEL: Record<DayState, string> = {
  done: 'erledigt',
  missed: 'verpasst',
  open: 'offen',
  off: 'nicht geplant',
  future: 'Zukunft',
  before: 'vor dem Start',
};

export interface MonthCalendarProps {
  habit: Habit;
  days: ReadonlySet<DayKey>;
  today: DayKey;
  /** Any day of the shown month. */
  month: DayKey;
  onMonth: (month: DayKey) => void;
  onToggle: (day: DayKey, done: boolean) => void;
}

export function MonthCalendar({ habit, days, today, month, onMonth, onToggle }: MonthCalendarProps) {
  const first = startOfMonth(month);
  const grid = monthGrid(first);
  const start = firstDay(habit, days);
  const target = habit.schedule.kind === 'weekly' ? habit.schedule.times : null;
  const canNext = addMonths(first, 1) <= startOfMonth(today);
  const swipe = useRef<{ x: number; y: number } | null>(null);

  const linked = (day: DayKey | undefined) => day !== undefined && sameMonth(day, first) && days.has(day);

  return (
    <section className={`cal glass hc-${habit.color}`} aria-label="Kalender">
      <header className="cal__head">
        <button type="button" className="icon-btn" onClick={() => onMonth(addMonths(first, -1))} aria-label="Vorheriger Monat">
          <ChevronLeft size={20} />
        </button>
        <h2 className="cal__title" aria-live="polite">
          {formatMonth(first)}
        </h2>
        <button type="button" className="icon-btn" onClick={() => onMonth(addMonths(first, 1))} disabled={!canNext} aria-label="Nächster Monat">
          <ChevronRight size={20} />
        </button>
      </header>
      <div
        className={`cal__grid${target ? ' cal__grid--weekly' : ''}`}
        onPointerDown={(event) => {
          swipe.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerUp={(event) => {
          const from = swipe.current;
          swipe.current = null;
          if (!from) return;
          const dx = event.clientX - from.x;
          if (Math.abs(dx) < 50 || Math.abs(event.clientY - from.y) > 40) return;
          if (dx > 0) onMonth(addMonths(first, -1));
          else if (canNext) onMonth(addMonths(first, 1));
        }}
      >
        {WEEKDAY_SHORT.map((label) => (
          <span key={label} className="cal__wd">
            {label}
          </span>
        ))}
        {target && <span className="cal__wd cal__wd--week">Woche</span>}
        {grid.map((row) => {
          const count = target ? weekCount(days, row[0]!) : 0;
          return (
            <Fragment key={row[0]}>
              {row.map((day, i) => {
                if (!sameMonth(day, first)) return <span key={day} className="cal__cell" />;
                const state = dayState(habit, days, day, today, start);
                const done = state === 'done';
                const cls = [
                  'cal__cell',
                  done && i > 0 && linked(row[i - 1]) ? 'cal__cell--link-l' : '',
                  done && i < 6 && linked(row[i + 1]) ? 'cal__cell--link-r' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <span key={day} className={cls}>
                    <button
                      type="button"
                      className={`cal__day cal__day--${state}${day === today ? ' cal__day--today' : ''}`}
                      disabled={day > today}
                      aria-pressed={done}
                      aria-label={`${formatFullDate(day)}: ${STATE_LABEL[state]}`}
                      onClick={() => onToggle(day, !done)}
                    >
                      {Number(day.slice(8))}
                    </button>
                  </span>
                );
              })}
              {target && row[0]! > today && <span className="cal__cell" />}
              {target && row[0]! <= today && (
                <span
                  className={`cal__week${count >= target ? ' cal__week--met' : ''}`}
                  aria-label={`Woche: ${Math.min(count, target)} von ${target}`}
                >
                  {count >= target ? <Check size={16} strokeWidth={3} /> : `${count}/${target}`}
                </span>
              )}
            </Fragment>
          );
        })}
      </div>
      <ul className="legend" aria-label="Legende">
        <li>
          <span className="legend__swatch legend__swatch--done" /> Erledigt
        </li>
        {habit.schedule.kind !== 'weekly' && (
          <li>
            <span className="legend__swatch legend__swatch--missed" /> Verpasst
          </li>
        )}
        <li>
          <span className="legend__swatch legend__swatch--open" /> Offen
        </li>
        {habit.schedule.kind === 'weekdays' && (
          <li>
            <span className="legend__swatch legend__swatch--off" /> Nicht geplant
          </li>
        )}
      </ul>
    </section>
  );
}
